import { useState, type ReactNode } from 'react';
import { Outlet, useLoaderData } from 'react-router-dom';
import { contracts } from '@immo/api-contract';
import type { WorkspaceShell } from '@/lib/auth/workspace-context';
import { isImpersonationSession } from '@/lib/auth/session';
import { formatDisplayName } from '@/lib/auth/display-name';
import { mayCall } from '@/lib/permissions/route-gate';
import { WorkspaceEventsProvider } from '@/lib/workspace-events/provider';
import { NavCountsProvider } from '@/lib/nav-counts';
import { ImpersonationBanner } from './impersonation-banner';
import { MailboxReconnectBanner } from './mailbox-reconnect-banner';
import { PortalReconnectBanner } from './portal-reconnect-banner';
import { NavigationProgressBar } from './navigation-progress-bar';
import { ActivitiesNavCount } from './activities-nav-count';
import { Sidebar } from './sidebar';
import { SandboxBanner } from './sandbox-banner';
import { TestWorkspaceBanner } from './test-workspace-banner';
import { Topbar } from './topbar';
import type { WorkspaceOutletContext } from './topbar-slots';
import { CommandPalette } from '@/components/search/command-palette';
import { CommandPaletteProvider, useCommandPalette } from '@/components/search/command-palette-provider';
import { GlobalSearchEntry } from '@/components/search/global-search-entry';
import { EmailComposeProvider } from '@/components/email-compose/email-compose-provider';
import { MainLandmarkProvider } from './main-landmark';

function TopbarSearchEntry() {
  const { openPalette } = useCommandPalette();

  return <GlobalSearchEntry variant="topbar" onClick={openPalette} />;
}

// Without a provider the event hooks are inert, so a caller who may not open the stream never asks for it.
function WorkspaceEvents({
  enabled,
  workspaceId,
  children,
}: {
  readonly enabled: boolean;
  readonly workspaceId: string;
  readonly children: ReactNode;
}) {
  if (!enabled) return <>{children}</>;

  return <WorkspaceEventsProvider workspaceId={workspaceId}>{children}</WorkspaceEventsProvider>;
}

export function WorkspaceLayout() {
  const {
    me,
    permissions,
    offeredRoles,
    organizations,
    narrowedOrganizationId,
    seesAllStandorte,
    workspaceId,
    workspaceSlug,
  } = useLoaderData() as WorkspaceShell;
  const [topbarEl, setTopbarEl] = useState<HTMLElement | null>(null);
  const [leadingEl, setLeadingEl] = useState<HTMLElement | null>(null);
  const [trailingEl, setTrailingEl] = useState<HTMLElement | null>(null);

  // Each shell request is made only by a caller who may call its route: a refused request is noise, and for an
  // HLRE member (no Standort) it is a request the server answers with 403 or 404.
  const mayStream = mayCall(permissions, contracts.workspaceEvents.stream);
  const mayCountActivities =
    mayCall(permissions, contracts.activities.ownOpenCount) &&
    mayCall(permissions, contracts.leads.activityOwnOpenCount);
  const maySearch = mayCall(permissions, contracts.search.query);

  const context: WorkspaceOutletContext = { me, workspaceId, permissions, topbarEl, leadingEl, trailingEl };

  // The active workspace's test flag + name come from the same `/auth/me`
  // payload the loader already resolved (grouped under `me.workspaces`).
  const activeWorkspace = me.workspaces.find((workspace) => workspace.id === workspaceId);

  // Below `requireWorkspace` (so the RESOLVED workspace UUID is available — the
  // `:workspaceId` route param is the slug) and above every workspace surface, so
  // the whole tab shares one connection.
  return (
    <WorkspaceEvents enabled={mayStream} workspaceId={workspaceId}>
      <CommandPaletteProvider>
        <EmailComposeProvider workspaceId={workspaceId} workspaceSlug={workspaceSlug} me={me} permissions={permissions}>
          {/* Above both the Sidebar and the Outlet: a page publishes its row
              count, the shell publishes Aktivitäten, and the nav row reads both. */}
          <NavCountsProvider>
            {mayCountActivities && <ActivitiesNavCount key={workspaceId} workspaceId={workspaceId} />}
            <div className="flex h-screen flex-col bg-canvas text-text-primary">
              <SandboxBanner />
              {isImpersonationSession() && <ImpersonationBanner memberName={formatDisplayName(me)} />}
              {activeWorkspace?.isTest === true && <TestWorkspaceBanner workspaceName={activeWorkspace.name} />}
              <MailboxReconnectBanner workspaceId={workspaceId} permissions={permissions} />
              <PortalReconnectBanner workspaceId={workspaceId} permissions={permissions} />
              <div className="flex min-h-0 flex-1">
                <Sidebar
                  me={me}
                  permissions={permissions}
                  offeredRoles={offeredRoles}
                  narrowedOrganizationId={narrowedOrganizationId}
                  seesAllStandorte={seesAllStandorte}
                  organizations={organizations}
                  workspaceId={workspaceId}
                />
                <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  <NavigationProgressBar />
                  <Topbar
                    onHeaderRef={setTopbarEl}
                    onLeadingRef={setLeadingEl}
                    onTrailingRef={setTrailingEl}
                    centre={maySearch ? <TopbarSearchEntry /> : undefined}
                  />
                  <main className="flex-1 overflow-y-auto bg-surface">
                    <MainLandmarkProvider>
                      <Outlet context={context} />
                    </MainLandmarkProvider>
                  </main>
                </div>
              </div>
            </div>
          </NavCountsProvider>
          {maySearch && <CommandPalette workspaceId={workspaceId} permissions={permissions} />}
        </EmailComposeProvider>
      </CommandPaletteProvider>
    </WorkspaceEvents>
  );
}
